import axios from 'axios';

const CART_STORE = 'asset';

/** How long a count is served from cache before a mount or focus refetches it. */
export const CART_COUNT_STALE_TIME_MS = 60_000;

const BASE_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 60_000;
/** Retries after the first failure; the badge stays hidden once exhausted. */
export const MAX_CART_COUNT_RETRIES = 5;

export type CartCountIdentity = {
  userId: string;
  organizationId: string;
};

/**
 * `ensureExists=false` is what keeps this a read: without it the bridge
 * creates an empty cart for the user, and every page view would mint one.
 */
export const buildCartCountPath = ({
  userId,
  organizationId,
}: CartCountIdentity): string =>
  `/commerce/bridge/v1/users/${encodeURIComponent(userId)}/organizations/${encodeURIComponent(organizationId)}/cart?store=${CART_STORE}&ensureExists=false`;

const statusOf = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

/** A guest cart, read by its id (CPS-7693); a GET never creates one. */
export const buildGuestCartPath = (cartId: string): string =>
  `/commerce/bridge/v1/cart/${encodeURIComponent(cartId)}`;

/**
 * `Retry-After` is either delay-seconds or an HTTP date (RFC 9110 §10.2.3);
 * a date is turned into the wait remaining from `now`.
 */
export const retryAfterMs = (error: unknown, now = Date.now()): number => {
  const header = axios.isAxiosError(error)
    ? error.response?.headers?.['retry-after']
    : undefined;
  if (typeof header !== 'string' || header.trim() === '') {
    return 0;
  }
  const seconds = Number(header);
  const ms = Number.isNaN(seconds) ? Date.parse(header) - now : seconds * 1000;
  return Number.isFinite(ms) && ms > 0 ? ms : 0;
};

/**
 * Exponential backoff for the 1-based retry React Query is about to make (1s,
 * 2s, 4s, … capped at 60s), stretched to the gateway's `Retry-After` when it asks for
 * longer.
 */
export const cartCountRetryDelay = (
  retryCount: number,
  error?: unknown
): number => {
  const retryAfter = retryAfterMs(error);
  // Retry-After may be hidden by CORS. Keep the five fallback retries spread
  // over at least a minute while preserving exponential backoff.
  const baseDelay =
    statusOf(error) === 429 && !retryAfter
      ? BASE_RETRY_DELAY_MS * 2
      : BASE_RETRY_DELAY_MS;
  return Math.max(
    Math.min(baseDelay * 2 ** (retryCount - 1), MAX_RETRY_DELAY_MS),
    retryAfter
  );
};

let readsHeldUntil = 0;

/**
 * Holds every cart-count read until `until` (epoch ms) — focus, cart-changed,
 * checkout return and remounts, not just React Query's scheduled retry — so none of
 * them spends another request against the rate limit before Retry-After or
 * the backoff has run out. `0` releases the hold.
 */
export const holdCartCountReads = (until: number): void => {
  readsHeldUntil = until;
};

/** Whether the mini-cart's cart reads are held. */
export const areCartCountReadsHeld = (now = Date.now()): boolean =>
  now < readsHeldUntil;

/** How long reads stay held from `now`; `0` once the hold is over. */
export const cartCountReadsHeldFor = (now = Date.now()): number =>
  Math.max(readsHeldUntil - now, 0);

/**
 * Rate limits, server errors and dropped connections can clear up on their
 * own; any other 4xx will not, so retrying it only burns rate limit.
 */
export const shouldRetryCartCount = (
  error: unknown,
  retryCount: number
): boolean => {
  if (retryCount > MAX_CART_COUNT_RETRIES) {
    return false;
  }
  const status = statusOf(error);
  return status === undefined || status === 429 || status >= 500;
};

/**
 * Returning from checkout with Back restores this page from bfcache with the
 * pre-checkout count in memory; an ordered cart reads as 404, so revalidating
 * here is what drops the badge to 0. A regular navigation back loads a fresh
 * document, which fetches on mount anyway.
 */
export const subscribeToPageRestore = (listener: () => void) => {
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      listener();
    }
  };
  window.addEventListener('pageshow', onPageShow);
  return () => window.removeEventListener('pageshow', onPageShow);
};

let checkoutDeparted = false;

/** The cart link can open checkout in another tab, leaving this page mounted. */
export const markCheckoutDeparture = () => {
  checkoutDeparted = true;
};

/** A read observed no cart lines, so a return from checkout has nothing left to pick up. */
export const clearCheckoutDeparture = () => {
  checkoutDeparted = false;
};

/** Refresh each return until a read observes an empty cart, even inside stale time. */
export const subscribeToCheckoutReturn = (listener: () => void) => {
  const onFocus = () => {
    if (checkoutDeparted) {
      listener();
    }
  };
  window.addEventListener('focus', onFocus);
  return () => window.removeEventListener('focus', onFocus);
};
